import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Landmark, Lock, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import AskCard from "@/components/ask/AskCard";
import { FUN_STYLES, SIMPLE_TEMPLATES, CARD_BGS, OCCASIONS, friendlyError, todayLocal, surpriseMessage, fillAmount, AMOUNT_FALLBACK } from "@/lib/askThemes";
import { fmtNaira } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const DRAFT = "debii_ask_draft";
const blank = (kind) => ({ kind, mode: "personal", approach: "fun", style: "urgent_2k", title: kind === "lend" ? "" : FUN_STYLES[0].title, message: kind === "lend" ? "" : fillAmount(FUN_STYLES[0].message, FUN_STYLES[0].fb || AMOUNT_FALLBACK), amountStr: kind === "lend" ? "" : FUN_STYLES[0].fb || AMOUNT_FALLBACK, fb: FUN_STYLES[0].fb, tpl: FUN_STYLES[0].message, occasion: "", card_bg: "emerald_mint", amount: "", target: "", hasTarget: true, show_progress: true, recipient_name: "", access_code: "", repay_date: "" });

function Chip({ active, onClick, children, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`rounded-full px-3.5 py-2 text-xs font-semibold transition-all ${active ? "bg-primary text-primary-foreground scale-[1.03] shadow-sm" : "bg-secondary text-muted-foreground"}`}>
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="mb-1.5 block text-xs font-semibold text-muted-foreground">{label}</span>{children}</label>;
}

function StylePicker({ d, set, phrase }) {
  // Picking a template deliberately replaces the text; the amount phrase is filled in live.
  const pick = (s, src = s) => {
    const p = phrase(src.fb);
    set({ style: s.id, title: s.title, message: fillAmount(src.message, p), amountStr: p, fb: src.fb || null, tpl: src.message, occasion: "" });
  };
  return (
    <div className="space-y-4">
      <div className="flex gap-1.5">
        {[["fun", "Fun & Dramatic"], ["simple", "Simple"], ["custom", "Custom"]].map(([id, l]) => (
          <Chip key={id} testId={`ask-approach-${id}`} active={d.approach === id} onClick={() => {
            if (id === "simple") set({ approach: id, style: "simple", title: "", message: "", occasion: "" });
            else if (id === "custom") set({ approach: id, style: "custom", title: "", message: "" });
            else { set({ approach: id }); pick(FUN_STYLES[0]); }
          }}>{l}</Chip>
        ))}
      </div>
      {d.approach === "fun" && (
        <div className="grid grid-cols-2 gap-2.5">
          {FUN_STYLES.map((s) => (
            <button key={s.id} type="button" data-testid={`style-card-${s.id}`} onClick={() => (s.id === "surprise_me" ? pick(s, surpriseMessage(d.tpl)) : pick(s))}
              className={`relative overflow-hidden rounded-2xl p-3 text-left text-white transition-transform active:scale-95 ${d.style === s.id ? "ring-[3px] ring-accent ring-offset-2 scale-[1.02]" : ""}`} style={{ background: s.bg }}>
              <span className="text-3xl">{s.emoji}</span>
              <p className="mt-1 text-sm font-bold leading-tight">{s.name}</p>
              {d.style === s.id && <Check className="absolute right-2 top-2 h-4 w-4" />}
            </button>
          ))}
        </div>
      )}
      {d.approach === "simple" && (
        <div data-testid="simple-templates" className="space-y-2.5">
          {SIMPLE_TEMPLATES.map((t) => (
            <button key={t.id} type="button" data-testid={`simple-template-${t.id}`} onClick={() => pick(t)}
              className={`relative flex w-full items-start gap-3 rounded-2xl border-2 p-3.5 text-left transition-all active:scale-[0.98] ${d.style === t.id ? "border-accent bg-emerald-50" : "border-border bg-card"}`}>
              <span className="h-11 w-11 shrink-0 rounded-xl flex items-center justify-center text-2xl" style={{ background: t.bg }}>{t.emoji}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold">{t.name}</span>
                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{t.message}</span>
              </span>
              {d.style === t.id && <Check className="h-4 w-4 shrink-0 text-accent" />}
            </button>
          ))}
        </div>
      )}
      {d.approach === "custom" && (
        <div className="space-y-3">
          <div className="flex gap-2">
            {CARD_BGS.map((b) => (
              <button key={b.id} type="button" data-testid={`card-bg-${b.id}`} onClick={() => set({ card_bg: b.id })}
                className={`h-12 flex-1 rounded-xl transition-transform ${d.card_bg === b.id ? "ring-[3px] ring-accent ring-offset-2 scale-105" : ""}`} style={{ background: b.bg }} aria-label={b.name} />
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {OCCASIONS.map((o) => (
              <Chip key={o.name} testId={`occasion-${o.name.toLowerCase().replace(/\s+/g, "-")}`} active={d.occasion === o.name} onClick={() => {
                if (d.occasion === o.name) return set({ occasion: "" });
                const p = phrase();
                set({ occasion: o.name, message: fillAmount(o.message, p), amountStr: p, fb: null, tpl: o.message, title: d.title || o.name });
              }}>{o.emoji} {o.name}</Chip>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BankStep({ account, onChange, kind }) {
  return account ? (
    <div data-testid="ask-bank-selected" className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center"><Landmark className="h-5 w-5 text-primary" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{account.account_name}</p>
          <p className="text-xs text-muted-foreground">{account.bank_name} · {account.account_number}</p>
        </div>
        <button type="button" data-testid="ask-bank-change" onClick={onChange} className="text-xs font-bold text-accent">Change</button>
      </div>
      <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900">{kind === "lend" ? "Your lender sees these details once they accept." : "Anyone with your Ask link can see these bank details."}</p>
    </div>
  ) : (
    <button type="button" data-testid="ask-bank-add" onClick={onChange} className="w-full rounded-2xl border-2 border-dashed border-border p-5 text-center">
      <Landmark className="mx-auto h-6 w-6 text-primary" />
      <p className="mt-2 text-sm font-semibold">Add your collection account</p>
      <p className="text-xs text-muted-foreground">So people know where to send money.</p>
    </button>
  );
}

export default function AskCreate() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const kind = params.get("kind") === "lend" ? "lend" : "gift";
  const location = useLocation();
  const { user } = useAuth();
  const [d, setD] = useState(() => {
    try { const s = JSON.parse(sessionStorage.getItem(DRAFT)); if (s?.kind === kind) return s; } catch { /* ignore */ }
    return blank(kind);
  });
  const steps = kind === "gift" ? ["type", "style", "details", "bank", "preview"] : ["details", "bank", "preview"];
  const [step, setStep] = useState(d._step || 0);
  const [busy, setBusy] = useState(false);
  const set = (p) => setD((x) => ({ ...x, ...p }));
  const { data: acct } = useQuery({ queryKey: ["repayment-account"], queryFn: async () => (await api.get("/repayment/account")).data.account });
  const { data: limits } = useQuery({ queryKey: ["ask-limits"], queryFn: async () => (await api.get("/asks/limits")).data, enabled: kind === "lend" });

  useEffect(() => { sessionStorage.setItem(DRAFT, JSON.stringify({ ...d, _step: step })); }, [d, step]);

  const currency = user?.default_currency || "NGN";
  const amountValue = kind === "gift" ? (d.mode === "open" ? (d.hasTarget ? Number(d.target) : 0) : Number(d.amount)) : 0;
  const phrase = (fb) => (amountValue > 0 ? fmtNaira(amountValue, currency) : fb || AMOUNT_FALLBACK);
  // Keep the amount inside the message in sync, even after the user edits the rest of the text.
  useEffect(() => {
    if (kind !== "gift") return;
    const next = phrase(d.fb);
    if (d.amountStr && d.amountStr !== next && d.message.includes(d.amountStr)) {
      set({ message: d.message.split(d.amountStr).join(next), amountStr: next });
    } else if (d.amountStr !== next) set({ amountStr: next });
  }, [amountValue, d.fb]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = steps[step];
  const preview = {
    kind, mode: d.mode, style: d.style, card_bg: d.card_bg, title: d.title, message: d.message, occasion: d.occasion,
    amount: kind === "gift" && d.mode === "personal" ? Number(d.amount) || null : null,
    target: d.mode === "open" && d.hasTarget ? Number(d.target) || null : null, confirmed_total: 0, show_progress: d.show_progress,
    lend: kind === "lend" ? { amount: Number(d.amount) || null, repay_date: d.repay_date } : null,
  };

  const valid = () => {
    if (cur === "style" && d.approach === "simple") return d.style.startsWith("simple_");
    if (cur === "details" && kind === "lend") return Number(d.amount) > 0 && d.repay_date;
    if (cur === "details" && kind === "gift" && (!d.title.trim() || (d.approach !== "custom" && !d.message.trim()))) return false;
    if (cur === "details" && d.mode === "open" && d.hasTarget) return Number(d.target) > 0;
    if (cur === "details" && d.approach === "custom") return d.title.trim().length > 0;
    if (cur === "bank") return !!acct;
    if (d.access_code && !/^\d{4}$/.test(d.access_code)) return false;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        kind, mode: kind === "lend" ? "personal" : d.mode, style: kind === "lend" ? "lend" : d.style,
        title: d.title || null, message: d.message || null, occasion: d.occasion || null,
        card_bg: d.style === "custom" ? d.card_bg : null, recipient_name: d.recipient_name || null,
        amount: Number(d.amount) > 0 && (kind === "lend" || d.mode === "personal") ? Number(d.amount) : null,
        target: d.mode === "open" && d.hasTarget ? Number(d.target) : null, show_progress: d.show_progress,
        access_code: d.access_code || null, repay_date: kind === "lend" ? d.repay_date : null,
      };
      const { data } = await api.post("/asks", body);
      sessionStorage.removeItem(DRAFT);
      qc.invalidateQueries({ queryKey: ["asks"] });
      navigate(`/asks/${data.id}?new=1`, { replace: true });
    } catch (e) {
      const err = apiError(e);
      toast.error(friendlyError(err));
    } finally { setBusy(false); }
  };

  const back = () => (step === 0 ? (sessionStorage.removeItem(DRAFT), navigate("/asks")) : setStep(step - 1));
  const heading = { type: "Who's it for?", style: "Pick your vibe", details: kind === "lend" ? "Lend Me details" : "Say it your way", bank: "Where should money go?", preview: "Looks good?" }[cur];

  return (
    <div data-testid="ask-create" className="debii-shell min-h-[100dvh] px-5 pt-5 pb-32">
      <div className="flex items-center gap-3">
        <button data-testid="ask-create-back" onClick={back} className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex flex-1 gap-1">{steps.map((s, i) => <span key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-accent" : "bg-secondary"}`} />)}</div>
      </div>
      <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.14em] text-accent">{kind === "gift" ? "🎁 Gift Me" : "🤝 Lend Me"}</p>
      <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tight">{heading}</h1>

      <div key={cur} className="mt-5 ask-pop space-y-4">
        {cur === "type" && (
          <div className="grid gap-3">
            {[["personal", "Personal Ask", "One person, one private link."], ["open", "Open Ask", "One link for groups, Status & socials."]].map(([m, t, l]) => (
              <button key={m} type="button" data-testid={`ask-mode-${m}`} onClick={() => set({ mode: m })}
                className={`rounded-2xl border-2 p-4 text-left transition-all ${d.mode === m ? "border-accent bg-emerald-50" : "border-border bg-card"}`}>
                <p className="font-display text-base font-bold">{t}</p><p className="text-xs text-muted-foreground">{l}</p>
              </button>
            ))}
          </div>
        )}
        {cur === "style" && <StylePicker d={d} set={set} phrase={phrase} />}
        {cur === "details" && (
          <>
            {kind === "lend" && limits?.lend_limit_reached && (
              <div data-testid="lend-limit-warning" className="flex gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <p>You're at the Free plan's {limits.free_limit} active debts. You won't be able to record this loan when it arrives unless you settle a debt or <button onClick={() => navigate("/upgrade")} className="font-bold underline">upgrade to Plus</button>.</p>
              </div>
            )}
            <Field label="Title"><Input data-testid="ask-title-input" value={d.title} maxLength={80} onChange={(e) => set({ title: e.target.value })} placeholder={kind === "lend" ? "Could you lend me?" : "Give it a title"} className="h-12 rounded-xl" /></Field>
            <Field label={kind === "lend" ? "Purpose (optional)" : "Message"}><Textarea data-testid="ask-message-input" value={d.message} maxLength={500} onChange={(e) => set({ message: e.target.value })} className="min-h-[90px] rounded-xl" /></Field>
            {(kind === "lend" || d.mode === "personal") && (
              <Field label={kind === "lend" ? "Amount to borrow (₦)" : "Amount (optional, ₦)"}><Input data-testid="ask-amount-input" inputMode="numeric" value={d.amount} onChange={(e) => set({ amount: e.target.value.replace(/[^\d]/g, "") })} className="h-12 rounded-xl font-display text-lg font-bold" /></Field>
            )}
            {kind === "lend" && <Field label="Repay by"><Input data-testid="ask-repay-date-input" type="date" min={todayLocal()} value={d.repay_date} onChange={(e) => set({ repay_date: e.target.value })} className="h-12 rounded-xl" /></Field>}
            {kind === "gift" && d.mode === "open" && (
              <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
                <div className="flex gap-1.5">
                  <Chip testId="ask-target-on" active={d.hasTarget} onClick={() => set({ hasTarget: true })}>Set a target</Chip>
                  <Chip testId="ask-target-off" active={!d.hasTarget} onClick={() => set({ hasTarget: false })}>Any amount is welcome</Chip>
                </div>
                {d.hasTarget && <>
                  <Input data-testid="ask-target-input" inputMode="numeric" placeholder="e.g. 20000" value={d.target} onChange={(e) => set({ target: e.target.value.replace(/[^\d]/g, "") })} className="h-12 rounded-xl font-display text-lg font-bold" />
                  <label className="flex items-center justify-between text-sm"><span>Show progress to contributors</span><Switch data-testid="ask-show-progress" checked={d.show_progress} onCheckedChange={(v) => set({ show_progress: v })} /></label>
                </>}
              </div>
            )}
            {(kind === "lend" || d.mode === "personal") && <>
              <Field label={kind === "lend" ? "Who are you asking?" : "Who's it for? (optional)"}><Input data-testid="ask-recipient-input" value={d.recipient_name} maxLength={60} onChange={(e) => set({ recipient_name: e.target.value })} className="h-12 rounded-xl" /></Field>
              <Field label={<span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Access code (optional, 4 digits)</span>}>
                <Input data-testid="ask-access-code-input" inputMode="numeric" maxLength={4} value={d.access_code} onChange={(e) => set({ access_code: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="h-12 rounded-xl tracking-[0.4em]" />
              </Field>
            </>}
          </>
        )}
        {cur === "bank" && <BankStep kind={kind} account={acct} onChange={() => navigate("/settings/repayment", { state: { returnTo: `${location.pathname}${location.search}` } })} />}
        {cur === "preview" && (
          <>
            <AskCard ask={preview} testId="ask-preview-card" />
            {acct && <p className="text-center text-xs text-muted-foreground">Recipients will see {acct.account_name} · {acct.bank_name} · {acct.account_number}</p>}
          </>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 mx-auto max-w-[30rem] bg-gradient-to-t from-background via-background to-transparent px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-6">
        {cur === "preview" ? (
          <Button data-testid="create-ask-submit-button" disabled={busy} onClick={submit} className="h-14 w-full rounded-2xl text-base font-bold">{busy ? "Creating…" : "Create & share"}</Button>
        ) : (
          <Button data-testid="ask-next-button" disabled={!valid()} onClick={() => setStep(step + 1)} className="h-14 w-full rounded-2xl text-base font-bold">Continue</Button>
        )}
      </div>
    </div>
  );
}
